import { defineStore } from 'pinia'
import httpClient from '@/api/httpClient'
import { withIDKeys } from '@/utils/state_helpers.js'
import { useMessagesStore } from '@/stores/messages'

export const useStoryMediaStore = defineStore('storyMedia', {
  state: () => ({
    storyId: null,
    storyMedia: {}
  }),

  getters: {
    attachments({ storyMedia }) {
      return Object.values(storyMedia)
        .filter(sm => !sm.use_as_content_for)
        .sort((a, b) => (a.list_order > b.list_order) ? 1 : -1)
    },

    noAttachments() {
      return this.attachments.length === 0
    },

    paths({ storyMedia }) {
      let paths = []
      for (let key in storyMedia) {
        if (storyMedia[key].medium) {
          paths.push(storyMedia[key].medium.relative_path)
        }
      }
      return paths
    },

    byContentFor: ({ storyMedia }) => (contentFor) => {
      for (let key in storyMedia) {
        if (storyMedia[key].use_as_content_for == contentFor) {
          return storyMedia[key]
        }
      }
    }
  },

  actions: {
    async fetchForStory(storyId) {
      this.storyId = storyId

      return httpClient.get(`/api/internal/stories/${storyId}/story_media`)
        .then(response => {
          this.storyMedia = withIDKeys(response.data.story_media)
        })
        .catch(err => {
          useMessagesStore().smartAdd(err)
        })
    },

    async create({ file, useAsContentFor, cutline, credit }) {
      let formData = new FormData()

      if (useAsContentFor) formData.append(`story_medium[use_as_content_for]`, useAsContentFor)
      formData.append(`story_medium[medium_attributes][upload_filename]`, file.name)
      formData.append(`story_medium[medium_attributes][upload]`, file)
      if (cutline) formData.append(`story_medium[medium_attributes][cutline]`, cutline)
      if (credit) formData.append(`story_medium[medium_attributes][credit]`, credit)

      return httpClient.post(`/api/internal/stories/${this.storyId}/story_media`, formData)
        .then(({ data: { story_medium } }) => {
          this.storyMedia = { ...this.storyMedia, [story_medium.id]: story_medium }
          return story_medium
        })
        .catch(err => {
          useMessagesStore().smartAdd(err)
          throw err
        })
    },

    async batchCreate({ storyIds, mediumIds }) {
      return httpClient.post('/api/internal/story_media/batch_create', {
        story_ids: storyIds,
        media_ids: mediumIds
      })
    },

    async createFromUnrecognized({ path }) {
      return httpClient.post(
        `/api/internal/stories/${this.storyId}/story_media`,
        { story_medium: { medium_attributes: { file: path } } }
      ).then(({ data: { story_medium } }) => {
        this.storyMedia = { ...this.storyMedia, [story_medium.id]: story_medium }
      })
      .catch(err => {
        useMessagesStore().smartAdd(err)
      })
    },

    async createFromMedium({ mediumId }) {
      return httpClient.post(
        `/api/internal/stories/${this.storyId}/story_media`,
        { story_medium: { medium_id: mediumId } }
      ).then(({ data: { story_medium } }) => {
        this.storyMedia = { ...this.storyMedia, [story_medium.id]: story_medium }
      })
      .catch(err => {
        useMessagesStore().smartAdd(err)
      })
    },

    async save() {
      if (Object.keys(this.storyMedia).length === 0) return

      return httpClient.patch(
        `/api/internal/stories/${this.storyId}/story_media/batch_update`,
        { story_media: withIDKeys(this.storyMedia) }
      ).then(({ data: { story_media } }) => {
        this.storyMedia = withIDKeys(story_media)
        useMessagesStore().addNotice('Attachments saved')
      }).catch(err => {
        useMessagesStore().smartAdd(err)
      })
    },

    async destroy({ storyMediumId }) {
      return httpClient.delete(`/api/internal/stories/${this.storyId}/story_media/${storyMediumId}`)
        .then(({ data: { message } }) => {
          const { [storyMediumId]: _removed, ...rest } = this.storyMedia
          this.storyMedia = rest
          useMessagesStore().addNotice(message)
        })
        .catch(err => {
          useMessagesStore().smartAdd(err)
        })
    },

    updateListOrder(newOrder) {
      newOrder.forEach(({ id, list_order }) => {
        if (this.storyMedia[id]) this.storyMedia[id].list_order = list_order
      })
    },

    update({ id, storyMedium }) {
      this.storyMedia = { ...this.storyMedia, [id]: storyMedium }
    },

    updateDestroy({ key, value }) {
      for (let id in this.storyMedia) {
        if (this.storyMedia[id].use_as_content_for === key) {
          this.storyMedia[id]._destroy = value
          break
        }
      }
    },

    moveToPosition({ storyMediumId, position }) {
      const attachments = this.attachments
      const currentItem = attachments.find(item => item.id === storyMediumId)

      if (!currentItem || attachments.length <= 1) {
        console.log('Cannot move - only one item or item not found')
        return
      }

      const currentItemIndex = attachments.findIndex(a => a.id === storyMediumId)
      const targetPosition = position === 'first' ? 1 : attachments.length

      const newOrder = attachments.map((item, index) => {
        if (item.id === storyMediumId) {
          return { id: item.id, list_order: targetPosition }
        } else {
          if (position === 'first') {
            if (index < currentItemIndex) {
              return { id: item.id, list_order: index + 2 }
            } else {
              return { id: item.id, list_order: index + 1 }
            }
          } else {
            if (index > currentItemIndex) {
              return { id: item.id, list_order: index }
            } else {
              return { id: item.id, list_order: index + 1 }
            }
          }
        }
      })

      this.updateListOrder(newOrder)
    }
  }
})
